import { EventEmitter, Injectable } from "@angular/core";
import { APN_LIST, CellularDevice, CellularDeviceHelper, CellularProviderStatuses } from "@app/cellular/models/cellular-device.model";
import { DevicesActions, ProjectActions } from "@app/project/store";
import { CellularDbService, CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { Action, TypedAction } from "@ngrx/store/src/models";
import { Observable, of, throwError } from "rxjs";
import { catchError, exhaustMap, filter, map, switchMap, take, tap } from "rxjs/operators";

import { MatDialog } from "@angular/material/dialog";
import { CHSecurityDevice } from "@app/project/model/ch-device";
import { Project } from "@app/project/model/project";
import { SecuritySystem } from "@app/project/model/security-system";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { RootStoreState } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { Store } from "@ngrx/store";

@Injectable({
    providedIn: "root",
})
export class CellularService {
    constructor(
        private readonly store: Store<RootStoreState.State>,
        private readonly cellularDbService: CellularDbService,
        private readonly tsLoggingService: TSLoggingService,
        private readonly projectDispatcher: ProjectDispatchers,
        private readonly deviceDispatchers: DeviceDispatchers,
        private readonly chServiceService: CHServiceService,
        private readonly dialog: MatDialog
    ) {}

    /**
     * @description Gets the cellular provider status (active, pre-active, etc) of a provided cellular device.
     * @param {CellularDevice} device the cellular device
     * @returns {CellularProviderStatuses} the status
     */
    getCellularDeviceStatus(device: CellularDevice): CellularProviderStatuses {
        // if provider is bmobile, automatically return unknown
        return device?.provider === CellularProviderType.BMOBILE
            ? CellularProviderStatuses.Unknown
            : device?.status || CellularProviderStatuses.Unknown;
    }

    /**
     * @description Checks to see if the normalized cellular device status is active.
     * @param {CellularDevice} device the device to check
     * @returns {boolean} is the device active?
     */
    isActive(device: CellularDevice): boolean {
        return this.getCellularDeviceStatus(device) === CellularProviderStatuses.Active;
    }

    /**
     * @description Takes a selected provider type and returns the provider name.
     * @param {CellularProviderType} providerType the provider type selected by the user
     * @returns {string} the cellular provider name
     */
    getCellularCarrierName(providerType: CellularProviderType) {
        return APN_LIST.find(provider => provider.type === providerType)?.label || "Unknown";
    }

    /**
     * @description Dispatches an error message
     * @param {string} errorMessage the error message
     */
    displayError(errorMessage: string) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(null, null, errorMessage, true),
            })
        );
    }

    /**
     * @description Dispatches a success action and sets the timeout to 5000ms
     * @param btnText
     * @param {string} message the message to display
     */
    displaySuccess(message: string, btnText?) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(null, null, message),
                buttonText: btnText || null,
                duration: 5000,
            })
        );
    }

    /**
     * @description Dispatches an action to update the current project in the store. Used here to update the security system
     * device on the project in the store after updating the cellular info.
     * @param {Project} currentProject the project to update
     */
    dispatchUpdateProject(currentProject: Project) {
        this.store.dispatch(new ProjectActions.UpdateProjectSuccess(currentProject));
    }

    /**
     * @description Create a cellular device using the cellular IDs from the HDM device and the database device.
     * The IDs from the HDM device will be used first, but overridden by the database IDs, if any are provided.
     * The IDs are then merged with a cellular device that has some default properties.
     * @param {CHSecurityDevice} securityDevice the HDM security device from the controller
     * @param {SecuritySystem} [dbSecurityDevice] the security system device in the database
     * @returns {CellularDevice} the newly created cellular device
     */
    getCellularDeviceProperties(securityDevice: CHSecurityDevice, dbSecurityDevice?: SecuritySystem): CellularDevice {
        let chCellInfo: Partial<CellularDevice> = {};
        let dbCellInfo: Partial<CellularDevice> = {};
        if (securityDevice?.Properties.MEID || securityDevice?.Properties.ICCID) {
            chCellInfo = {
                meid: securityDevice.Properties.MEID,
                imei: securityDevice.Properties.IMEI,
                iccid: securityDevice.Properties.ICCID,
            };
        }

        if (dbSecurityDevice?.meid || dbSecurityDevice?.iccid) {
            dbCellInfo = {
                meid: dbSecurityDevice.meid,
                imei: dbSecurityDevice.imei,
                iccid: dbSecurityDevice.iccid,
            };
        }

        if (dbSecurityDevice?.cellularProvider) {
            dbCellInfo.provider = dbSecurityDevice.cellularProvider;
        }

        return {
            ...CellularDeviceHelper.getDefault(),
            ...chCellInfo,
            ...dbCellInfo,
        };
    }

    // TODO: Move to action/effect/reducer. This info is now required by multiple components.
    /**
     * @description Fetch the cellular status from the 3rd party provider
     * @param {string} iccid the device containing the IDs to look up
     * @param {CellularProviderType} provider the 3rd party rpovider to query
     * @param {boolean} displayError
     * @returns {Observable<CellularDevice>} the cellular device, with status
     */
    fetchCellularDevice(iccid: string, provider: CellularProviderType, displayError = true): Observable<CellularDevice> {
        if (iccid && provider && provider !== CellularProviderType.NONE && !this.isBmobile(provider)) {
            return this.cellularDbService.queryDevice$(iccid, provider).pipe(
                map(response => this.parseCellularDeviceResponse(iccid, provider, response)),
                catchError((error: { error: Error }) => {
                    if (displayError) {
                        const errMsg = `There was a problem contacting the cellular provider
                        The ICCID: ${iccid} is not registered with the selected provider.`;
                        this.displayError(errMsg);
                    }

                    return throwError(() => error);
                })
            );
        }
        return of(null);
    }

    isBmobile(cellularProvider: CellularProviderType) {
        return cellularProvider === CellularProviderType.BMOBILE;
    }

    /**
     * @description Parses the response from a get cellular device call.
     * Mutates the response by adding the provider from the source device.
     * Displays an error if the device is not found.
     * @private
     * @param {string} iccid the iccid submitted
     * @param {CellularProviderType} provider the provider used
     * @param {CellularDevice} response the response from the API call
     * @returns {CellularDevice} the updated cellualr device info, including the provider form the original device
     */
    private parseCellularDeviceResponse(iccid: string, provider: CellularProviderType, response: CellularDevice): CellularDevice {
        if (response && Object.keys(response).length && iccid) {
            response.provider = provider;
            // Do not use IMEI in response from provider. AT&T does not return an IMEI that meets the DB restrictions
            delete response.imei;
            return CellularDeviceHelper.fromDbObject(response);
        }
        this.displayError(`Your device could not be found in ${provider || "the cellular provider"}'s system.`);
        return null;
    }

    /**
     * @description Logs a cellular event in the time series database
     * @param {string} action the event to log
     * @param {Record<string, any>} data the data to log
     */
    public logCellularEvent(action: string, data: Record<string, any>) {
        this.tsLoggingService.logEvent(null, "cellular", "ssopgrest", action, null, data);
    }

    /**
     * @description Dispatches an action
     * @param {Action} action the action to dispatch
     */
    public dispatchAction(action: Action) {
        this.store.dispatch(action);
    }

    /**
     * @description Dispatches an action to upsert the security system on a project
     * @param {Project} project the project to update if the security system doesn't exist
     * @param {SecuritySystem} securitysystem the security system to update
     * @param {EventEmitter<any>} callbackEmitter the listener for when the action is complete
     */
    public dispatchUpsertSecuritySystem(project: Project, securitysystem: SecuritySystem, callbackEmitter: EventEmitter<any>) {
        this.projectDispatcher.dispatchUpsertSecuritySystem(project, securitysystem, callbackEmitter);
    }

    /**
     * @description Activates a cellular device with a 3rd party cellular provider.
     * @param {SecuritySystem} securitysystem the security system device that will contain the cellular info to activate
     * @param {string} zipcode the zip code required by Verizon to activate
     * @param {Project} project the database ID of the project to update when complete
     * @param {CellularProviderType} provider the cellular provider to activate the device with
     * @returns {Observable<any>} the security system device produced as a result of the activation
     */
    activateCellularDevice(
        securitysystem: SecuritySystem,
        zipcode: string,
        project: Project,
        provider: CellularProviderType
    ): Observable<Action | (TypedAction<any> & DataServiceError<any>)> {
        const rawBodyContent = { iccid: securitysystem.iccid, zipcode };
        this.logCellularEvent("activate", securitysystem);

        // Attempt to update the cellular data in the database first. If there is a problem with an ICCID conflict,
        // we'll know before the SIM is activated.
        const emitter = new EventEmitter();

        // Emitter should be returned before its used
        setTimeout(() => {
            this.projectDispatcher.dispatchUpsertSecuritySystem(project, securitysystem, emitter);
        });

        return emitter.pipe(
            tap((result: TypedAction<any> & DataServiceError<any>) => {
                if (result?.type.includes("Failure")) {
                    throw result.error;
                }
            }),
            exhaustMap(result =>
                this.cellularDbService.activateDevice$(rawBodyContent, provider).pipe(
                    tap(() => {
                        this.store.dispatch(result);
                    })
                )
            ),
            catchError((error: Error) => {
                this.displayError(
                    `Device activation was not successful.\n
                    Error: ${error.message}`
                );
                return throwError(() => error);
            })
        );
    }

    /**
     * @description Dispatches an action to save the security system on a project
     * @param {Project} project the database ID of the project to update when complete
     * @param {SecuritySystem} securitysystem The Security system to save
     * @returns {Observable<any>} the security system device produced as a result of the save action
     */
    saveCellularDeviceToDb(project: Project, securitysystem: SecuritySystem) {
        const emitter = new EventEmitter();
        this.projectDispatcher.dispatchUpsertSecuritySystem(project, securitysystem, emitter);
        return emitter.pipe(
            tap(result => {
                this.store.dispatch(result);
            }),
            catchError((error: Error) => {
                this.displayError(
                    `Device was not saved.\n
                    Error: ${error.message}`
                );
                return throwError(() => error);
            })
        );
    }

    /**
     * @description Deactivates a cellular device with a 3rd party cellular provider.
     * @param {string} iccid the ICCID to deactivate
     * @param {CellularProviderType} provider the 3rd party cellular provider
     * @returns {Observable<any>} the response from the 3rd party
     */
    deactivateCellularDevice(iccid: string, provider: CellularProviderType): Observable<{ result: boolean }> {
        const rawBodyContent = { iccid };
        this.logCellularEvent("deactivate", rawBodyContent);
        return this.cellularDbService.deactivateDevice$(rawBodyContent, provider).pipe(
            tap(_ => {
                let message = "Device deactivation successful";
                let btnText = null;
                if (provider === CellularProviderType.VERIZON) {
                    message = "Device deactivation submitted. It may take up to 24 hours to complete.";
                    btnText = "OK";
                }
                this.displaySuccess(message, btnText);
            }),
            catchError((error: Error) => {
                this.displayError(
                    `Device deactivation was not successful.\n
                    Error: ${error.message}`
                );
                return throwError(() => error);
            })
        );
    }

    updateCellularDevice(
        project: Project,
        cellularProvider: CellularProviderType,
        isErrorToastHidden = false,
        isOfflineAccepted = false,
        ignoreIDCheck = false
    ) {
        const uuid = ProjectHelperService.getProjectUuid(project);
        const emitter = new EventEmitter();
        return this.chServiceService.getSecurityPanel(uuid).pipe(
            map(services => services?.[0]),
            // Update panel provider
            switchMap(chSecurityDevice => {
                const partitionUid = chSecurityDevice?.ChildUIDs?.[0] || null;
                if (!partitionUid) {
                    return of(chSecurityDevice);
                }
                return this.deviceDispatchers.selectDeviceClassObjectState(partitionUid, DEVICE_CLASSES.CELLULAR_QUALITY_DCO).pipe(
                    tap(state => {
                        if (state === LoadingState.init) {
                            this.deviceDispatchers.getDeviceClassObject({
                                uuid: ProjectHelperService.getProjectUuid(project),
                                deviceUid: partitionUid,
                                dco: DEVICE_CLASSES.CELLULAR_QUALITY_DCO,
                                ignoreIDCheck,
                            });
                        }
                    }),
                    filter(state => state === LoadingState.loaded),
                    take(1),
                    map(() => chSecurityDevice)
                );
            }),
            switchMap(chSecurityDevice => {
                const partitionUid = chSecurityDevice?.ChildUIDs?.[0] || null;
                this.deviceDispatchers.dispatchUpdateDeviceClassObject({
                    uuid,
                    deviceUid: partitionUid,
                    deviceClass: DEVICE_CLASSES.CELLULAR_QUALITY_DCO,
                    property: "carrier",
                    args: cellularProvider.toUpperCase(),
                    hideErrorToast: isErrorToastHidden,
                    callbackEmitter: emitter,
                    ignoreIDCheck,
                });
                return emitter;
            }),
            // Throw error if panel offline
            map(action => {
                if (action.type === DevicesActions.ActionTypes.UpdateDeviceClassObjectFailure) {
                    let msg = action.error.error;
                    if (msg.includes("EOF")) {
                        msg = "The controller is not connected to the ClareCloud server.";
                        if (isOfflineAccepted) {
                            return null;
                        }
                    }
                    throw new Error(msg);
                } else {
                    return null;
                }
            }),
            // Update isCompleted in db
            exhaustMap(_ => {
                const securitySystem = project.securitysystem;
                if (securitySystem) {
                    const update = { ...securitySystem, cellularProvider, isCompleted: true };
                    return this.saveCellularDeviceToDb(project, update);
                }
                return of(null);
            })
        );
    }
}
